import { useEffect, useRef } from 'react';
/**
 * Creates and manages a portal container DOM element.
 * The container is appended to document.body on mount and removed on unmount.
 *
 * @param id - Unique ID for the portal container element
 * @returns The portal container element (stable across renders), or null on first render
 *
 * @example
 * ```tsx
 * const portal = usePortal('my-drawer-portal');
 * if (!portal) return null;
 * return createPortal(<MyDrawer />, portal);
 * ```
 */ export function usePortal(id) {
    const elRef = useRef(null);
    if (!elRef.current && typeof document !== 'undefined') {
        // Reuse existing portal container if one exists (e.g., from HMR)
        const existing = document.getElementById(id);
        if (existing) {
            elRef.current = existing;
        } else {
            const div = document.createElement('div');
            div.id = id;
            elRef.current = div;
        }
    }
    useEffect(()=>{
        const el = elRef.current;
        if (!el) return;
        // Only append if not already in the DOM
        if (!el.parentElement) {
            document.body.appendChild(el);
        }
        return ()=>{
            // Only remove if we're the ones who added it and it's empty
            if (el.parentElement && el.childNodes.length === 0) {
                el.parentElement.removeChild(el);
            }
        };
    }, [
        id
    ]);
    return elRef.current;
}
